import { Info } from "lucide-react";

import { formatAmount } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapFormat";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckbox } from "@/shared/components/forms/ColocaCheckbox";
import useTranslations from "@/shared/lang/useTranslations";
import { subtractDecimals } from "@/shared/utils/decimal";

import type { UseCardCopTopUpResult } from "../../hooks/useCardCopTopUp";
import { useCopTopUpFormat } from "./copTopUpFormat";

export interface CopTopUpReviewProps {
  flow: UseCardCopTopUpResult;
  /**
   * Set when the card's organization has no card wallet: the credit can never be withdrawn, only moved to
   * another card, so the confirmation waits for the reader to say they understood.
   */
  acknowledgement?: { checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean };
}

export function CopTopUpReview({ flow, acknowledgement }: CopTopUpReviewProps) {
  const { t, activeLanguage } = useTranslations("cards");
  const format = useCopTopUpFormat();
  const fee = flow.delivered && flow.credited ? subtractDecimals(flow.delivered, flow.credited) : null;
  const percent = formatAmount(flow.percent, activeLanguage, { min: 0, max: 4 });

  const rows: { label: string; value: string; strong?: boolean }[] = [
    { label: t("cop_top_up_row_pay"), value: format(flow.debit, "COP"), strong: true },
    { label: t("cop_top_up_row_price"), value: format(flow.quote?.price.value, "COP") },
    { label: t("cop_top_up_row_sent"), value: format(flow.delivered, "USDT") },
    { label: t("cop_top_up_row_fee", { percent }), value: fee ? `− ${format(fee, "USD")}` : "—" },
    {
      label: t("cop_top_up_row_credit"),
      value: flow.credited ? `≈ ${format(flow.credited, "USD")}` : "—",
      strong: true,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <dl className="flex flex-col divide-y divide-border rounded-xl border border-border bg-card">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-muted-foreground">{row.label}</dt>
            <dd
              className={
                row.strong
                  ? "font-mono text-sm font-semibold tabular-nums text-foreground"
                  : "font-mono text-sm tabular-nums text-foreground"
              }
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">{t("cop_top_up_approx_note")}</p>
      <ColocaAlert variant="warning" icon={Info} text={t("cop_top_up_irreversible")} />
      {acknowledgement && (
        <ColocaCheckbox
          checked={acknowledgement.checked}
          disabled={acknowledgement.disabled}
          onCheckedChange={acknowledgement.onChange}
          label={t("cop_top_up_ack_no_withdraw")}
        />
      )}
    </div>
  );
}
